





























import { Vue, Component, Prop, Watch } from "vue-property-decorator";

import {
  LayerMaterials,
  Edges,
  LeftSideLayer,
  RightSideLayer
} from "../types/TrialPitLayers";

import {
  drawShapeBackground,
  drawEdges,
  drawDivider,
  drawMeasurements,
  drawText,
  scale,
  drawPitBottom,
  drawMeasurementLine,
  drawFeature,
  drawSample
} from "../utility/CanvasHelper";
import { PitDetailModel } from "./TrialPitInput/PitDetails.vue";
import { TrialPitFeatureModel } from "./TrialPitInput/TrialPitFeature.vue";
import { TrialPitTestModel } from "./TrialPitInput/TrialPitTest.vue";
import { StrataBelowRefChoices } from "../types/TrialPitDetails";

interface ComponentOptions {
  background: CanvasImageSource | null;
  edgeType: string;
  topWidth: number;
  bottomWidth: number;
  height: number;
  text: string;
  hMeasurements: string;
  vMeasurements: string;
  layerLine: string;
  currentTop: number;
  currentEdge: number;
  diagramEdge: number;
  roughWidth: number;
  roughVariance: number;
  currentMeasuredWidth: number;
  currentMeasuredHeight: number;
  maxWidthLeft: number;
  maxWidthRight: number;
  originalEdge: number;
  holeWidth: number;
  totalWidth: number;
  totalDepth: number;
}

@Component({})
export default class TrialPitDrawing extends Vue {
  @Prop({ default: 150 }) private heightAboveGround!: number;
  @Prop({ default: 100 }) private heightBelowGround!: number;
  @Prop({
    default: () => ({
      width: 0,
      height: 0,
      depth: 0,
      groundLevel: 0,
      groundLevelLabel: "GROUND LEVEL",
      aboveGroundMaterial: "brick",
      strataBelowRef: "Bore Hole"
    })
  })
  private pitDetail!: PitDetailModel;
  @Prop({ default: () => [] }) private leftSideLayers!: LeftSideLayer[];
  @Prop({ default: () => [] }) private rightSideLayers!: RightSideLayer[];
  @Prop({ default: () => [] }) private features!: TrialPitFeatureModel[];
  @Prop({ default: () => [] }) private tests!: TrialPitTestModel[];

  @Prop({ default: () => true }) private redrawRequired!: boolean;

  private allImagesReady: boolean = false;
  private images: Array<{ key: string; img: CanvasImageSource }> = [];
  private screenWidth: number = 320;

  private graphHeight: number = 1280;
  private graphWidth: number = 900;

  public getImageFromCanvas() {
    const graph = this.$refs.graph as HTMLCanvasElement;
    return graph.toDataURL();
  }

  protected async mounted() {
    this.screenWidth = window.screen.availWidth;

    window.onresize = (e: any) => {
      if (e.target.screen.availWidth !== this.screenWidth) {
        this.screenWidth = e.target.screen.availWidth;
        this.drawTrialPit();
      }
    };

    this.images = await Promise.all<{ key: string; img: CanvasImageSource }>(
      LayerMaterials.map(
        m =>
          new Promise(r => {
            const image = new Image();
            image.onload = () => {
              r({ key: m.value, img: image });
            };
            let fileName = m.value.toLowerCase();
            if (parseInt(fileName, 10)) {
              fileName = fileName + "l";
            }

            this.getImgUrl(fileName).then((src: { default: string }) => {
              image.src = src.default;
            });
          })
      )
    );

    this.allImagesReady = true;

    this.drawTrialPit();
  }

  protected beforeDestroy() {
    window.onresize = null;
  }

  @Watch("redrawRequired")
  protected doRedraw() {
    if (this.redrawRequired) {
      this.drawTrialPit();
    }
  }

  protected drawTrialPit() {
    if (!this.allImagesReady) {
      return;
    }
    const graph = this.$refs.graph as HTMLCanvasElement;

    if (
      graph &&
      this.pitDetail.width > 0 &&
      this.pitDetail.height > 0 &&
      this.pitDetail.depth > 0
    ) {
      this.clearCanvas(graph);

      const maxWidthLeft = this.leftSideLayers.reduce(
        (p, c) => p + c.topStep + c.bottomStep,
        0
      );
      const maxWidthRight = this.rightSideLayers.reduce(
        (p, c) => p + c.topStep + c.bottomStep,
        100
      );

      const totalDepth =
        this.pitDetail.depth + this.heightAboveGround + this.heightBelowGround;
      const totalWidth = 300 + this.pitDetail.width;

      let currentTop = 50;
      let currentEdge = 150;
      let currentMeasuredHeight = 0;
      let currentMeasuredWidth = 0;

      // first draw above ground
      this.drawComponent(graph, {
        background: this.images.find(
          i => i.key === this.pitDetail.aboveGroundMaterial
        )!.img,
        edgeType: "straight",
        topWidth: 0,
        bottomWidth: 0,
        height: this.heightAboveGround,
        text: "",
        hMeasurements: "none",
        vMeasurements: "none",
        layerLine: "none",
        currentTop,
        currentEdge,
        diagramEdge: 0,
        roughWidth: 30,
        roughVariance: 6,
        currentMeasuredWidth,
        currentMeasuredHeight,
        maxWidthLeft,
        maxWidthRight,
        originalEdge: 100,
        holeWidth: 800,
        totalWidth,
        totalDepth
      });

      // draw the measurement for the pit dimensions
      drawMeasurementLine(
        graph.getContext("2d")!,
        currentEdge,
        totalWidth - currentEdge,
        50,
        `${this.pitDetail.width}x${this.pitDetail.height}`,
        totalWidth,
        totalDepth,
        this.graphWidth,
        this.graphHeight
      );

      currentTop += this.heightAboveGround;

      // draw all left side layers
      for (const layer of this.leftSideLayers) {
        this.drawComponent(graph, {
          background: this.images.find(i => i.key === layer.layerType)!.img,
          edgeType: layer.edgeType,
          topWidth: layer.topStep,
          bottomWidth: layer.bottomStep,
          height: layer.layerDepth,
          text: layer.label,
          hMeasurements: layer.horizontalMeasurement,
          vMeasurements: layer.verticalMeasurement,
          layerLine: layer.layerDivider,
          currentTop,
          currentEdge,
          diagramEdge: 0,
          roughWidth: 30,
          roughVariance: 6,
          currentMeasuredWidth,
          currentMeasuredHeight,
          maxWidthLeft,
          maxWidthRight,
          originalEdge: 100,
          holeWidth: 800,
          totalWidth,
          totalDepth
        });

        if (layer.verticalMeasurement === "defer") {
          currentMeasuredHeight += layer.layerDepth;
        } else {
          currentMeasuredHeight = 0;
        }

        if (layer.horizontalMeasurement === "logTop") {
          currentMeasuredWidth = layer.bottomStep;
        } else if (layer.horizontalMeasurement === "logBottom") {
          currentMeasuredWidth = 0;
        } else {
          currentMeasuredWidth += layer.topStep + layer.bottomStep;
        }

        currentEdge += layer.topStep + layer.bottomStep;
        currentTop += layer.layerDepth;
      }

      const currentLeftDepth = this.leftSideLayers.reduce(
        (p, c) => p + c.layerDepth,
        0
      );

      if (currentLeftDepth < this.pitDetail.depth) {
        const fillToBase = this.pitDetail.depth - currentLeftDepth;

        this.drawComponent(graph, {
          background: this.images.find(i => i.key === "slab")!.img,
          edgeType: "solid",
          topWidth: 0,
          bottomWidth: 0,
          height: fillToBase,
          text: "",
          hMeasurements: "none",
          vMeasurements: "none",
          layerLine: "dotted",
          currentTop,
          currentEdge,
          diagramEdge: 0,
          roughWidth: 30,
          roughVariance: 6,
          currentMeasuredWidth,
          currentMeasuredHeight,
          maxWidthLeft,
          maxWidthRight,
          originalEdge: 100,
          holeWidth: 800,
          totalWidth,
          totalDepth
        });

        currentTop += fillToBase;
      }

      // draw below ground
      drawPitBottom(
        graph.getContext("2d")!,
        scale(currentTop, totalDepth, this.graphHeight),
        scale(currentEdge, totalWidth, this.graphWidth),
        scale(totalWidth - maxWidthRight - 50, totalWidth, this.graphWidth)
      );

      // reset top
      currentTop = 50 + this.heightAboveGround + this.pitDetail.groundLevel;
      currentEdge = totalWidth - 150;
      currentMeasuredHeight = 0;

      // first draw above ground right side
      this.drawComponent(graph, {
        background: this.images[1].img,
        edgeType: "none",
        topWidth: 0,
        bottomWidth: 0,
        height: this.heightAboveGround,
        text: "",
        hMeasurements: "none",
        vMeasurements: "none",
        layerLine: "none",
        currentTop: 50,
        currentEdge,
        diagramEdge: totalWidth,
        roughWidth: 30,
        roughVariance: 6,
        currentMeasuredWidth,
        currentMeasuredHeight,
        maxWidthLeft,
        maxWidthRight,
        originalEdge: currentEdge,
        holeWidth: 800,
        totalWidth,
        totalDepth
      });

      // draw the ground level label
      drawText(
        graph.getContext("2d")!,
        `${this.pitDetail.groundLevelLabel} (${this.pitDetail.groundLevel}mm)`,
        scale(totalWidth, totalWidth, this.graphWidth),
        scale(currentEdge, totalWidth, this.graphWidth),
        scale(0, totalWidth, this.graphWidth),
        scale(currentTop - 100, totalDepth, this.graphHeight),
        scale(100, totalDepth, this.graphHeight)
      );

      // draw all right side layers
      for (const layer of this.rightSideLayers) {
        const label = `${layer.label ? layer.label + "\n" : ""}${
          layer.rootsObserved === "No Roots"
            ? "NO ROOTS OBSERVED" : layer.rootsObserved === "None" ? ""
            : layer.rootsObserved.toUpperCase() + " "
        }${
          layer.rootsObserved === "Roots" && layer.rootsMaxDiameter !== null
            ? "TO " + layer.rootsMaxDiameter + "mm⌀ "
            : ""
        }`;
        this.drawComponent(graph, {
          background: this.images.find(i => i.key === layer.layerType)!.img,
          edgeType: layer.edgeType,
          topWidth: -layer.topStep,
          bottomWidth: -layer.bottomStep,
          height: layer.layerDepth,
          text: label,
          hMeasurements: layer.horizontalMeasurement,
          vMeasurements: layer.verticalMeasurement,
          layerLine: layer.layerDivider,
          currentTop,
          currentEdge,
          diagramEdge: totalWidth,
          roughWidth: 30,
          roughVariance: 6,
          currentMeasuredWidth,
          currentMeasuredHeight,
          maxWidthLeft,
          maxWidthRight,
          originalEdge: totalWidth - 100,
          holeWidth: 800,
          totalWidth,
          totalDepth
        });

        if (layer.verticalMeasurement === "defer") {
          currentMeasuredHeight += layer.layerDepth;
        } else {
          currentMeasuredHeight = 0;
        }

        currentEdge -= layer.topStep + layer.bottomStep;
        currentTop += layer.layerDepth;
      }

      for (const feature of this.features) {
        drawFeature(
          graph.getContext("2d")!,
          feature.type,
          feature.label,
          feature.alignment,
          feature.orientation,
          feature.position,
          feature.shape,
          scale(feature.x1, totalWidth, this.graphWidth),
          scale(feature.x2, totalWidth, this.graphWidth),
          scale(feature.y1, totalDepth, this.graphHeight),
          scale(feature.y2, totalDepth, this.graphHeight),
          scale(150, totalWidth, this.graphWidth),
          scale(50 + this.heightAboveGround, totalDepth, this.graphHeight),
          scale(feature.depth, totalDepth, this.graphHeight),
          scale(feature.width, totalWidth, this.graphWidth),
          scale(feature.height, totalDepth, this.graphHeight),
          this.pitDetail.width,
          this.graphWidth
        );
      }

      for (const test of this.tests) {
        drawSample(
          graph.getContext("2d")!,
          test.testType,
          scale(test.distanceFromWall, totalWidth, this.graphWidth),
          scale(
            test.depthOfSample + this.heightAboveGround,
            totalDepth,
            this.graphHeight
          ),
          test.testResults
        );
      }

      // draw text for strata below ref here
      const logText = StrataBelowRefChoices.find(
        c => c.value === this.pitDetail.strataBelowRef
      );
      drawText(
        graph.getContext("2d")!,
        logText ? logText.graphText(this.pitDetail.depth) : "",
        scale(0, totalWidth, this.graphWidth),
        scale(totalWidth, totalWidth, this.graphWidth),
        scale(0, totalWidth, this.graphWidth),
        scale(totalDepth - 20, totalDepth, this.graphHeight),
        scale(0, totalDepth, this.graphHeight)
      );
    }

    this.$emit("redrawPitComplete");
  }

  private getImgUrl(name: string) {
    const images = require.context(
      "../assets/agsTiles/",
      false,
      /\.png$/,
      "lazy-once"
    );
    return images(`./${name}.png`);
  }

  private scale(input: number) {
    return input * 2;
  }

  private random(seed: number) {
    const x = Math.sin(seed++) * 10000;
    return x - Math.floor(x);
  }

  private clearCanvas(graph: HTMLCanvasElement) {
    const ctx = graph.getContext("2d")!;
    ctx.clearRect(0, 0, graph.width, graph.height);
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, graph.width, graph.height);
    ctx.restore();
  }

  private drawComponent(graph: HTMLCanvasElement, options: ComponentOptions) {
    const {
      background,
      topWidth,
      bottomWidth,
      height,
      text,
      hMeasurements,
      vMeasurements,
      layerLine,
      diagramEdge,
      roughWidth,
      roughVariance,
      maxWidthLeft,
      maxWidthRight,
      originalEdge,
      holeWidth,
      totalWidth,
      totalDepth,
      currentTop,
      currentEdge,
      currentMeasuredWidth,
      currentMeasuredHeight
    } = options;
    let { edgeType } = options;

    const top = currentTop;
    const dottedEdge = currentEdge;
    const seed = 1;

    const ctx = graph.getContext("2d")!;

    if (!Edges.find(e => e.value === edgeType)) {
      edgeType = "straight";
    }

    if (background !== null) {
      drawShapeBackground(
        ctx,
        background,
        scale(diagramEdge, totalWidth, this.graphWidth),
        scale(top, totalDepth, this.graphHeight),
        edgeType as "straight" | "roughEdge" | "roughTop" | "roughAll" | "none",
        scale(currentEdge, totalWidth, this.graphWidth),
        scale(topWidth, totalWidth, this.graphWidth),
        scale(roughWidth, totalWidth, this.graphWidth),
        roughVariance,
        scale(bottomWidth, totalWidth, this.graphWidth),
        scale(height, totalDepth, this.graphHeight)
      );
    }

    drawEdges(
      ctx,
      scale(diagramEdge, totalWidth, this.graphWidth),
      scale(top, totalDepth, this.graphHeight),
      edgeType as "straight" | "roughEdge" | "roughTop" | "roughAll" | "none",
      scale(currentEdge, totalWidth, this.graphWidth),
      scale(topWidth, totalWidth, this.graphWidth),
      scale(roughWidth, totalWidth, this.graphWidth),
      roughVariance,
      scale(bottomWidth, totalWidth, this.graphWidth),
      scale(height, totalDepth, this.graphHeight)
    );

    drawDivider(
      ctx,
      layerLine as "dotted" | "solid" | "none",
      scale(diagramEdge, totalWidth, this.graphWidth),
      scale(currentEdge, totalWidth, this.graphWidth),
      scale(top, totalDepth, this.graphHeight)
    );

    drawMeasurements(
      ctx,
      topWidth,
      bottomWidth,
      currentEdge,
      diagramEdge,
      top,
      height,
      holeWidth,
      originalEdge,
      maxWidthLeft,
      maxWidthRight,
      currentMeasuredWidth,
      currentMeasuredHeight,
      hMeasurements as
        | "both"
        | "top"
        | "bottom"
        | "combined"
        | "logTop"
        | "logBottom",
      vMeasurements as "show" | "defer" | "none",
      totalWidth,
      totalDepth,
      this.graphWidth,
      this.graphHeight
    );

    drawText(
      ctx,
      text,
      scale(diagramEdge, totalWidth, this.graphWidth),
      scale(currentEdge, totalWidth, this.graphWidth),
      scale(topWidth, totalWidth, this.graphWidth),
      scale(top, totalDepth, this.graphHeight),
      scale(height, totalDepth, this.graphHeight)
    );
  }
}
